import { useState, useEffect, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { getTeamMembersWithPagination } from "services";
import { useSelector } from "react-redux";
import TeamMembersTable from "./TeamMembersTable";

export type tableData = {
  [x: string]: any;
  id: string;
};

export default function BankerTeams() {
  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleGetTeamMembers = useCallback(
    async (shouldShowNextPage: boolean) => {
      if (isFetchingMore || (shouldShowNextPage && page >= totalPages)) return;

      setIsFetchingMore(true);
      const currentPage = shouldShowNextPage ? page + 1 : 1;

      try {
        if (!shouldShowNextPage) setIsLoading(true);
        let limitBy: any = {
          per_page: 200,
          page: currentPage,
        };
        let res: any = await getTeamMembersWithPagination(
          limitBy,
          currentBranch?.team["teams*uuid"]
        );

        setTotalPages(res.meta.total_page);
        setPage(currentPage);

        setMembers((prev) =>
          shouldShowNextPage ? [...prev, ...res.data] : res.data
        );

        if (currentPage < totalPages) {
          handleGetTeamMembers(true);
        }
      } catch (error) {
        console.error("Error fetching auction members:", error);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [page, totalPages, isFetchingMore, currentBranch]
  );

  useEffect(() => {
    if (page <= totalPages) {
      handleGetTeamMembers(true);
    }
  }, [page]);

  useEffect(() => {
    handleGetTeamMembers(false);
  }, [currentBranch]);

  const columns: ColumnDef<{
    index: number;
    email: any;
    role: any;
    uuid: any;
  }>[] = [
    {
      accessorKey: "index",
      header: "Sr no.",
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1 hover:cursor-pointer">
            <p className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
              {row.getValue("index")}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1 hover:cursor-pointer">
            <p className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
              {row.getValue("email")}
            </p>
          </div>
        );
      },
    },

    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1 hover:cursor-pointer">
            <p className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
              {row.getValue("role")}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "uuid",
      header: "ID",
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1 hover:cursor-pointer">
            <p className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
              {row.getValue("uuid")}
            </p>
          </div>
        );
      },
    },
  ];

  const filteredRecords = members?.map((member: any, index: number) => {
    const emailObject = member?.attributes?.find(
      (attr: any) => attr.type === "email"
    );
    return {
      index: index + 1,
      email: emailObject?.value ? emailObject?.value : "N/A",
      role: member?.permissions?.role ? member?.permissions?.role : "N/A",
      uuid: member?.uuid,
    };
  });

  return (
    <div className="px-4 lg:px-8 py-6">
      <div className="flex justify-between">
        <p className="font-openSans font-normal text-web-subheading leading-6 text-brand-bgBlack">
          Teams
        </p>
      </div>
      <div className="mt-4 ">
        <TeamMembersTable
          data={filteredRecords}
          loading={isLoading}
          filterComponent={<></>}
          columns={columns}
        />
      </div>
    </div>
  );
}
